import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Card,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import {
  email_template_list,
  get_client_email_setting,
  update_client_email_setting,
  update_email_template,
} from "../../DAL/jobs/jobs";
import CircularLoader from "../../components/loader/CircularLoader";
import RecordNoImage from "../../components/recordNotFound/RecordNoImage";
import EditEmailTemplate from "./components/EditEmailTemplate";
import {
  convertTimeToServerTimeZone,
  show_proper_words,
} from "../../utils/Constant";
import { useIds } from "../../hook/AdminContext";
import { useParams } from "react-router-dom";
import CustomConfirmation from "../../components/popUp/CustomConfirmation";
import { select_icon } from "../../assets";
import TopBar from "../features/FeatureTopBar/components/TopBar";

const topBarList = [
  {
    id: "accept_job_template",
    title: "Accept Job Template",
  },
  {
    id: "reject_job_template",
    title: "Reject Job Template",
  },
];
export default function JobSettings() {
  const { userInfo, setBreadCrumb, adminSetting } = useIds();
  const { source_slug, jobtype_slug } = useParams();
  const [loading, setLoading] = useState(false);
  const [tabToOpen, setTabToOpen] = useState("accept_job_template");
  const [resonseObj, setResonseObj] = useState(null);
  // ================================
  //  All inputs object in one place
  // ================================
  const [inputs, setInputs] = useState({
    emailSendType: "manual",
    autoSendTime: dayjs(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [templates, setTemplates] = useState([]);
  const [filterTemplates, setFilterTemplates] = useState([]);
  console.log(templates, "templates")
  const [showChangeTypeWarning, setShowChangeTypeWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  // Reusable handler
  const handleChange = (key, value) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const updateTemplate = (id, updatedData) => {
    setTemplates((prev) =>
      prev.map((temp) => (temp._id === id ? { ...temp, ...updatedData } : temp))
    );
  };
  useEffect(() => {
    if (templates.length > 0) {
      const filtered = templates.filter(
        (item) => item.type === tabToOpen
      );
      setFilterTemplates(filtered);
    }
  }, [tabToOpen, templates]);
  const handleClickSubmit = (e) => {
    e?.preventDefault();

    // ============================
    // 1️⃣ VALIDATE ALL TEMPLATES
    // ============================
    for (let temp of templates) {
      if (!temp.email_message || temp.email_message.trim() === "") {
        enqueueSnackbar(
          `Template "${show_proper_words(temp.type)}" must have a body.`,
          {
            variant: "error",
          }
        );
        return;
      }
    }

    if (
      inputs.emailSendType == "immediate" &&
      resonseObj?.mail_sent_type == "manual"
    ) {
      setWarningMessage(
        "After this change, any candidates in the Accepted or Rejected lists with unsent emails will get emails automatically."
      );
      setShowChangeTypeWarning(true);
      return;
    }

    if (
      inputs.emailSendType == "auto" &&
      resonseObj?.mail_sent_type == "manual"
    ) {
      setWarningMessage(
        "After this change, any candidates in the Accepted or Rejected lists who still have unsent emails will automatically be scheduled to receive those emails at the newly selected scheduled time."
      );
      setShowChangeTypeWarning(true);
      return;
    }
    if (
      inputs.emailSendType == "manual" &&
      resonseObj?.mail_sent_type == "auto"
    ) {
      setWarningMessage(
        "After this change, any candidates in the Accepted or Rejected lists with unsent emails will no longer receive emails at the previously scheduled time, since the setting has now been changed to Manual."
      );
      setShowChangeTypeWarning(true);
      return;
    }
    if (
      inputs.emailSendType == "immediate" &&
      resonseObj?.mail_sent_type == "auto"
    ) {
      setWarningMessage(
        "After this change, any candidates in the Accepted or Rejected lists who still have unsent emails will get emails automatically."
      );
      setShowChangeTypeWarning(true);
      return;
    }
    handleSubmit(e);
  };
  const handleSubmit = async (e) => {
    e?.preventDefault();
    setShowChangeTypeWarning(false);
    setLoading(true);
    try {
      // ==================================================
      // 2️⃣ PREPARE ALL API CALLS (email settings + templates)
      // ==================================================
      const apiCalls = [
        updateEmailSettings(), // email settings API
        ...templates.map((temp) => updateEmailTemplate(temp)), // all template APIs
      ];

      // ===============================
      // 3️⃣ RUN ALL API CALLS PARALLEL
      // ===============================
      const responses = await Promise.all(apiCalls);

      const allSuccess = responses.every((res) => res?.code === 200);

      if (allSuccess) {
        enqueueSnackbar("Email settings updated successfully!", {
          variant: "success",
        });
      }
    } catch (error) {
      enqueueSnackbar("Something went wrong while updating.", {
        variant: "error",
      });
    } finally {
      setLoading(false); // 🔵 END LOADING
    }
  };

  const updateEmailSettings = async () => {
    const postData = {
      send_time: dayjs(inputs.autoSendTime).format("HH:mm"),
      mail_sent_type: inputs.emailSendType,
      user_timezone: inputs.timezone,
    };

    const resp = await update_client_email_setting(postData);
    if (resp.code !== 200) {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    setResonseObj((prev) => ({
      ...prev,
      mail_sent_type: inputs.emailSendType,
    }));
    return resp;
  };

  const updateEmailTemplate = async (temp) => {
    const postData = {
      title: temp.title,
      cc_email: temp.cc_email,
      email_subject: temp.email_subject,
      email_message: temp.email_message,
      status: temp.status,
      client_id: userInfo.profile_id,
    };

    const resp = await update_email_template(temp._id, postData);
    if (resp.code !== 200) {
      enqueueSnackbar(resp.message, { variant: "error" });
    }
    return resp;
  };

  // ===================================================
  //  LOAD EMAIL SETTINGS (using DAL)
  // ===================================================
  const loadEmailSetting = async () => {
    try {
      const response = await get_client_email_setting();

      if (response?.code !== 200) {
        enqueueSnackbar(response?.message || "Failed to load email settings", {
          variant: "error",
        });
        return;
      }

      const data = response.data;
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      setResonseObj(response.data);
      setInputs((old) => ({
        ...old,
        emailSendType: data?.mail_sent_type || "manual",
        autoSendTime: data?.send_time
          ? dayjs(
            convertTimeToServerTimeZone(
              data.send_time,
              adminSetting?.time_zone,
              userTimeZone
            ),
            "HH:mm"
          )
          : dayjs(),
        timezone: userTimeZone, // fallback
      }));
    } catch (error) {
      enqueueSnackbar("Error loading email setting", { variant: "error" });
      console.log("Email Setting Error:", error);
    }
  };

  // ===================================================
  //  LOAD EMAIL TEMPLATES (using DAL)
  // ===================================================
  const loadEmailTemplates = async () => {
    try {
      const response = await email_template_list();
      console.log(response, "responseresponseresponse");

      if (response?.code !== 200) {
        enqueueSnackbar(response?.message || "Failed to load email templates", {
          variant: "error",
        });
        return;
      }

      setTemplates(response.data.email_templates || []);
    } catch (error) {
      enqueueSnackbar("Error loading email templates", { variant: "error" });
      console.log("Templates Error:", error);
    }
  };

  // Load all data on mount
  useEffect(() => {
    setLoading(true);
    if (adminSetting?.time_zone && userInfo) {
      Promise.all([loadEmailSetting(), loadEmailTemplates()]).finally(() =>
        setLoading(false)
      );
    }
  }, [adminSetting?.time_zone, userInfo]);

  useEffect(() => {
    if (source_slug) {
      setBreadCrumb([
        {
          name: `${source_slug}`,
          path: `/${source_slug}/${jobtype_slug}/my-job-post`,
          active: false,
        },
        {
          name: `Job Settings`,
          path: `/${source_slug}/${jobtype_slug}/job-settings`,
          active: false,
        },
      ]);
    } else {
      setBreadCrumb([
        {
          name: `My Job Post`,
          path: `/my-job-posts`,
          active: false,
        },
        {
          name: `Job Settings`,
          path: `/job-settings`,
          active: false,
        },
      ]);
    }
  }, []);

  return (
    <>
      {showChangeTypeWarning && (
        <CustomConfirmation
          open={showChangeTypeWarning}
          setOpen={setShowChangeTypeWarning}
          headerHeading={"Pending Emails"}
          title={"Are you sure you want to change email send type?"}
          handleAgree={() => handleSubmit()}
          additionalInfo={warningMessage}
          infoTitle={"Warning"}
        />
      )}
      <div className="container px-lg-4 mt-3">
        <div className="row mt-0">
          <div className="col-12 heading-main p-style">
            <h2>Job Email Templates</h2>
            <p>
              Adjust job email preferences and define the templates for
              automated messages.
            </p>
          </div>
          <div>
            <TopBar
              classes="topbar-job-setting"
              items={topBarList}
              cardToOpen={tabToOpen}
              setCardToOpen={(value) => {
                setTabToOpen(value);
              }}
            />

          </div>
          {loading ? (
            <div className="text-center mt-5">
              <CircularLoader />
            </div>
          ) : (
            <div className="job-card">
              <form onSubmit={handleClickSubmit} className="row">
                {/* ========================
                EMAIL SETTINGS SECTION
              ======================== */}
                <h3>
                  {tabToOpen === "accept_job_template"
                    ? "Edit Accept Job Template"
                    : "Edit Reject Job Template"}
                </h3>
                <div className="col-12 col-md-6">
                  <label className="label-style">Email Send Type*</label>
                  {/* Email Send Type */}
                  <div className="custom-input-design">
                    <div className="position-relative">
                      <select
                        value={inputs.emailSendType}
                        label="Email Send Type"
                        required
                        className="ps-3"
                        onChange={(e) =>
                          handleChange("emailSendType", e.target.value)
                        }
                      >
                        <option value="auto">Schedule</option>
                        <option value="manual">Manual</option>
                        <option value="immediate">Immediately</option>
                      </select>
                      <img
                        src={select_icon}
                        className="input-end-icon pe-none"
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                {inputs.emailSendType == "auto" && (
                  <div className="col-12 col-md-6">
                    <label className="label-style">Schedule Send Time *</label>
                    <LocalizationProvider
                      className="hide-scrollbar"
                      dateAdapter={AdapterDayjs}
                    >
                      <TimePicker
                        value={inputs.autoSendTime}
                        onChange={(newValue) =>
                          handleChange("autoSendTime", newValue)
                        }
                        slotProps={{
                          textField: {
                            required: true,
                            fullWidth: true,
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                height: "50px",
                                borderRadius: "8px",
                                backgroundColor: "var(--portal-bg-secondary-color)",
                                fontSize: "14px",

                                "& fieldset": {
                                  borderColor: "var(--light-border-color)",
                                },

                                "&:hover fieldset": {
                                  borderColor: "var(--light-border-color)",
                                  borderWidth: "1px"
                                },

                                "&.Mui-focused fieldset": {
                                  borderColor: "var(--light-border-color)",
                                  borderWidth: "1px"
                                },
                              },

                              "& input": {
                                padding: "8px 12px",
                              },
                            },
                          },
                          popper: {
                            sx: {
                              "& .MuiList-root::-webkit-scrollbar": {
                                display: "none",
                              },
                              "& .MuiList-root": {
                                "-ms-overflow-style": "none",
                                "scrollbar-width": "none",
                              },
                            },
                          },
                        }} s
                      />

                    </LocalizationProvider>
                  </div>
                )}

                {/* ========================
                EMAIL TEMPLATE SECTION
              ======================== */}

                <>
                  {templates.length === 0 ? (
                    <div className="mt-5">
                      <RecordNoImage title="No Email Templates Found." />
                    </div>
                  ) : (
                    <>
                      {filterTemplates.map((temp, index) => (
                        <div key={temp._id} className="col-12">
                          <EditEmailTemplate
                            template={temp}
                            setTemplate={(updatedObj) =>
                              updateTemplate(temp._id, updatedObj)
                            }
                          />
                        </div>
                      ))}
                    </>
                  )}
                </>
                <div className="d-flex justify-content-end mt-3 mt-lg-4">
                  <button className="small-button-cotain-view-all">
                    Update Settings
                  </button>
                </div>
              </form>
            </div>
          )}
        </div >
      </div >
    </>
  );
}
